import { useState, useCallback, useRef, useEffect } from 'react';
import { checkInLocationService } from '@/api/domains/checkInLocation';
import type { CheckInLocationItem } from '@/api/domains/checkInLocation';


const PAGE_SIZE = 20;

export interface UseBulkDisableModalOptions {
  isSelectAllMode: boolean;
  filters?: {
    hasGeolocation?: boolean | null;
    isRemoteWork?: boolean | null;
    searchText?: string | null;
    excludeLocationIds?: number[];
  };
}

export interface UseBulkDisableModalResult {
  allItems: CheckInLocationItem[];
  isLoadingMore: boolean;
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
}

export function useBulkDisableModal({
  isSelectAllMode,
  filters,
}: UseBulkDisableModalOptions): UseBulkDisableModalResult {
  const pageRef = useRef(1);
  const hasMoreRef = useRef(true);
  const isLoadingMoreRef = useRef(false);
  const [allItems, setAllItems] = useState<CheckInLocationItem[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const loadMore = useCallback(async () => {
    if (!isSelectAllMode || isLoadingMoreRef.current || !hasMoreRef.current) return;
    isLoadingMoreRef.current = true;
    setIsLoadingMore(true);
    try {
      const response = await checkInLocationService.list({
        page: pageRef.current,
        pageSize: PAGE_SIZE,
        isActive: true,
        hasGeolocation: filters?.hasGeolocation,
        isRemoteWork: filters?.isRemoteWork,
        searchText: filters?.searchText,
      });
      if (response.success && response.data) {
        const excludedIds = new Set(filters?.excludeLocationIds ?? []);
        const fetchedItems = excludedIds.size > 0
          ? response.data.items.filter((item) => !excludedIds.has(item.id))
          : response.data.items;
        setAllItems((prev) => [...prev, ...fetchedItems]);
        hasMoreRef.current = response.data.items.length === PAGE_SIZE;
        pageRef.current += 1;
      }
    } finally {
      isLoadingMoreRef.current = false;
      setIsLoadingMore(false);
    }
  }, [
    isSelectAllMode,
    filters?.hasGeolocation,
    filters?.isRemoteWork,
    filters?.searchText,
    filters?.excludeLocationIds,
  ]);

  useEffect(() => {
    if (isSelectAllMode) void loadMore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el || !isSelectAllMode) return;

    const handleScroll = () => {
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 80) {
        void loadMore();
      }
    };

    el.addEventListener('scroll', handleScroll);
    return () => el.removeEventListener('scroll', handleScroll);
  }, [isSelectAllMode, loadMore]);

  return {
    allItems,
    isLoadingMore,
    scrollContainerRef,
  };
}

export default useBulkDisableModal;
